/**
 * Hook for seeding demo connections in embed/browser mode.
 *
 * When the app is loaded in browser mode, this hook:
 * 1. Seeds all 3 demo database connections if none exist
 * 2. Does NOT auto-connect - user must connect via the connections dialog
 */

import { useEffect, useRef } from 'react';
import { isBrowserMode } from '../browserModeUtils';
import { BROWSER_MODE_DATABASES } from '../types/browserModeTypes';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import type { Connection } from '@/types/electron';

export function useEmbedAutoConnect() {
  const hasSeeded = useRef(false);
  const { connections, loading: connectionsLoading } = useConnectionStore();

  useEffect(() => {
    // Only run in browser mode
    if (!isBrowserMode()) {
      return;
    }

    // Only seed once
    if (hasSeeded.current) {
      return;
    }

    // Wait for connections to finish loading
    if (connectionsLoading) {
      return;
    }

    hasSeeded.current = true;

    // Seed demo connections if none exist (don't auto-connect)
    const seedDemoConnections = async () => {
      const connectionStore = useConnectionStore.getState();

      // Seed demo connections if none exist
      if (connectionStore.connections.length === 0) {
        console.log('[useEmbedAutoConnect] Seeding demo connections');

        for (const demoDb of BROWSER_MODE_DATABASES) {
          const demoConnection: Connection = {
            id: demoDb.id,
            engine: 'sqlite' as const,
            server: 'browser-demo',
            port: '',
            database: demoDb.name,
            alias: demoDb.name,
            authentication: 'No password' as const,
          };
          await connectionStore.addConnection(demoConnection);
        }
        console.log('[useEmbedAutoConnect] Demo connections seeded, user can now connect via dialog');
      }
    };

    // Small delay to let the app initialize
    const timer = setTimeout(seedDemoConnections, 100);
    return () => clearTimeout(timer);
  }, [
    connections,
    connectionsLoading,
  ]);
}
